import React, { Suspense, useMemo, useRef, useState, useEffect } from 'react';
import { Text } from '@react-three/drei';
import WorldLabel from './WorldLabel';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useWorldStore } from '../state/worldStore';
import { proxied } from '../services/assets';
import { AnimatedResident } from './AnimatedResident';

// Peers — renders the OTHER people in your current area (worldStore.presences, fed by
// services/worldSocket). Members get a billboarded headshot (their artist avatar) +
// name; guests get a generic soft figure. Each peer's broadcast `pos` is the peer's
// CAMERA position (eye height), so the figure hangs its head at pos and body below.
// Positions arrive ~8 Hz — we lerp for smoothness.

// Member headshot as a texture (crossOrigin so WebGL accepts it; proxied so GCS
// images come through the API with CORS headers). Renders nothing until loaded.
function Headshot({ url, size = 0.42, position = [0, 0, 0] }) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    if (!url) return undefined;
    let dead = false;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(proxied(url), (t) => { if (!dead) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; setTex(t); } }, undefined, () => {});
    return () => { dead = true; };
  }, [url]);
  if (!tex) return null;
  return (
    <group position={position}>
      {/* soft backing so a transparent/edge headshot still reads as a token */}
      <mesh position={[0, 0, -0.01]}>
        <circleGeometry args={[size * 0.62, 32]} />
        <meshBasicMaterial color="#120b06" toneMapped={false} />
      </mesh>
      <mesh>
        <planeGeometry args={[size, size]} />
        <meshBasicMaterial map={tex} transparent toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

function Peer({ peer }) {
  const ref = useRef();
  const bodyRef = useRef();
  const nameRef = useRef();
  const npcList = useWorldStore((s) => s.npcList);

  // A member driving an avatar that has a rigged roster model → render their REAL
  // body (walks/idles, faces heading). Everyone else keeps the marker (headshot
  // token). Matched by avatar_id against the same roster the NPCs use.
  const rig = useMemo(
    () => (peer.member && peer.avatarId ? (npcList || []).find((n) => n.avatarId === peer.avatarId && n.rigged) : null),
    [npcList, peer.member, peer.avatarId],
  );

  // FLOOR-ANCHORED: use only the peer's X/Z, plant on the ground (y=0 in every
  // room). The broadcast pos.y is eye height and varies per room.
  const target = useRef(new THREE.Vector3(peer.pos?.[0] || 0, 0, peer.pos?.[2] || 0));
  if (peer.pos) target.current.set(peer.pos[0], 0, peer.pos[2]);
  useEffect(() => { if (ref.current) ref.current.position.copy(target.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Walk vs idle: flip `moving` from raw position deltas (feeds the rigged body's
  // clip). Turns off ~350ms after the last movement.
  const [moving, setMoving] = useState(false);
  const movedAt = useRef(0);
  const prev = useRef(null);
  useEffect(() => {
    if (!peer.pos) return;
    if (prev.current) {
      const dx = peer.pos[0] - prev.current[0], dz = peer.pos[2] - prev.current[2];
      if (dx * dx + dz * dz > 0.0004) { movedAt.current = performance.now(); setMoving(true); }
    }
    prev.current = peer.pos;
  }, [peer.pos]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame(({ camera }) => {
    const g = ref.current; if (!g) return;
    g.position.lerp(target.current, 0.2);
    const camY = Math.atan2(camera.position.x - g.position.x, camera.position.z - g.position.z);
    // Rigged body faces its HEADING (a real body walking); the marker faces the
    // viewer so the headshot reads. +Math.PI: the rig's forward axis is -Z.
    if (bodyRef.current) bodyRef.current.rotation.y = rig ? (peer.heading || 0) + Math.PI : camY;
    if (nameRef.current) nameRef.current.rotation.y = camY; // name always readable
    if (moving && performance.now() - movedAt.current > 350) setMoving(false);
  });

  // Proximity chat bubble — shows the peer's latest line for a few seconds.
  const [bubble, setBubble] = useState(null);
  useEffect(() => {
    if (!peer.say?.text) return undefined;
    setBubble(peer.say.text);
    const t = setTimeout(() => setBubble(null), 6000);
    return () => clearTimeout(t);
  }, [peer.say?.ts]);

  const isMember = peer.member && peer.avatarUrl;
  const label = peer.handle || (peer.member ? 'Member' : 'Guest');
  const topY = rig ? (rig.height || 1.8) + 0.25 : 1.15;

  return (
    <group ref={ref}>
      <group ref={bodyRef}>
        {rig ? (
          <Suspense fallback={null}>
            <AnimatedResident
              meshUrl={rig.rigged}
              clipUrls={[rig.walk, rig.idle].filter(Boolean)}
              play={moving ? 'walk' : 'idle'}
              height={rig.height || 1.8}
              position={[0, 0.08, 0]}
            />
          </Suspense>
        ) : (
          <>
            {/* marker — slim body planted on the floor + headshot on the front */}
            <mesh position={[0, 0.53, 0]} castShadow>
              <capsuleGeometry args={[0.18, 0.7, 6, 14]} />
              <meshStandardMaterial color={isMember ? '#3a2f4a' : '#2e2a3a'} roughness={0.75} metalness={0.12} />
            </mesh>
            {isMember && <Headshot url={peer.avatarUrl} position={[0, 0.72, 0.21]} />}
          </>
        )}
      </group>
      {/* Name always faces the viewer, above the head. */}
      <group ref={nameRef}>
        <Text position={[0, topY, 0]} fontSize={0.14} color="#ffe9c4" anchorX="center" anchorY="middle" outlineWidth={0.006} outlineColor="#000">
          {label}
        </Text>
      </group>
      {/* Speech bubble (proximity chat) — DOM overlay, auto-faces the camera. */}
      {bubble && (
        <WorldLabel position={[0, topY + 0.5, 0]} center distanceFactor={9} occlude={false} zIndexRange={[20, 0]} style={{ pointerEvents: 'none' }}>
          <div style={{ maxWidth: 180, background: 'rgba(16,10,6,0.92)', border: '1px solid rgba(255,178,94,0.5)', color: '#ffe9c4', font: '600 13px system-ui, sans-serif', padding: '6px 10px', borderRadius: 12, textAlign: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.4)', wordBreak: 'break-word' }}>
            {bubble}
          </div>
        </WorldLabel>
      )}
    </group>
  );
}

export default function Peers() {
  const presences = useWorldStore((s) => s.presences);
  const list = useMemo(() => (presences || []).filter((p) => Array.isArray(p.pos)), [presences]);
  return (
    <group>
      {list.map((p) => <Peer key={p.id || p.userId} peer={p} />)}
    </group>
  );
}
